import { useCallback, useEffect, useState } from "react";
import {
  AppShell,
  ActionIcon,
  Box,
  Group,
  Text,
  NavLink,
  Menu,
  ScrollArea,
  Stack,
  UnstyledButton,
  Avatar,
  Divider,
  Badge,
  Image,
  Alert,
  Anchor,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconLayoutDashboard,
  IconBriefcase2,
  IconLogout,
  IconUser,
  IconUsers,
  IconFileDescription,
  IconShieldCheck,
  IconAlertCircle,
  IconChartLine,
  IconShieldLock,
  IconDatabase,
  IconHistory,
  IconCoins,
  IconMail,
  IconX,
  IconSearch,
  IconAddressBook,
  IconWorld,
  IconSparkles,
} from "@tabler/icons-react";
import type { TablerIcon } from "@tabler/icons-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import trpc from "@/shared/utils/trpc";
import "mantine-react-table/styles.css";

type DecodedToken = {
  id: string;
  email: string;
  role: string;
  exp: number;
};

function AdminNavMenuClosedIcon({ size = 22 }: { size?: number }) {
  const lineWidth = Math.round(size * 0.85);
  const lineHeight = Math.max(2, Math.round(size / 9));
  const gap = Math.max(3, Math.round(size / 7));
  return (
    <Stack
      gap={gap}
      justify="center"
      align="center"
      aria-hidden
      style={{ width: size, height: size }}
    >
      {[0, 1, 2].map((key) => (
        <Box
          key={key}
          w={lineWidth}
          h={lineHeight}
          bg="currentColor"
          style={{ borderRadius: 1, flexShrink: 0 }}
        />
      ))}
    </Stack>
  );
}

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);

  const [verificationQueueTotal, setVerificationQueueTotal] = useState<number | null>(null);

  const refreshVerificationQueue = useCallback(async () => {
    if (!Cookies.get("token")) {
      setVerificationQueueTotal(null);
      return;
    }
    try {
      const data = await trpc.admin.verificationQueueSummary.query();
      setVerificationQueueTotal(data.total);
    } catch {
      setVerificationQueueTotal(null);
    }
  }, []);

  useEffect(() => {
    void refreshVerificationQueue();
    const intervalId = window.setInterval(() => void refreshVerificationQueue(), 90_000);
    return () => window.clearInterval(intervalId);
  }, [refreshVerificationQueue]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refreshVerificationQueue();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [refreshVerificationQueue]);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  // Get admin email from token
  const adminToken = Cookies.get("token");
  let adminEmail = "admin@gtconnect.com";
  if (adminToken) {
    try {
      const decoded = jwtDecode<DecodedToken>(adminToken);
      adminEmail = decoded.email;
    } catch (e) {
      // Ignore decode errors
    }
  }

  const isActive = (path: string, exact?: boolean) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const navItems: {
    label: string;
    icon: TablerIcon;
    path: string;
    badgeCount?: number | null;
    exact?: boolean;
  }[] = [
    { label: "Users", icon: IconUsers, path: "/admin/users" },
    { label: "Outer users", icon: IconAddressBook, path: "/admin/outer-users", exact: true },
    { label: "Credit history", icon: IconCoins, path: "/admin/credit-history" },
    { label: "Bulk mail", icon: IconMail, path: "/admin/bulk-mail", exact: true },
    { label: "Bulk mail history", icon: IconHistory, path: "/admin/bulk-mail/history" },
    { label: "Onboarding chart", icon: IconChartLine, path: "/admin/company-onboarding" },
    { label: "Countries map", icon: IconWorld, path: "/admin/countries-map" },
    { label: "Companies", icon: IconLayoutDashboard, path: "/admin/companies" },
    {
      label: "Verification queue",
      icon: IconShieldCheck,
      path: "/admin/pending-verification",
      badgeCount: verificationQueueTotal,
    },
    { label: "Indirect registry", icon: IconDatabase, path: "/admin/indirect-verification" },
    { label: "Search index (Meilisearch)", icon: IconSearch, path: "/admin/meilisearch", exact: true },
    { label: "Collaborations", icon: IconBriefcase2, path: "/admin/collaborations" },
    { label: "Interest Management", icon: IconUser, path: "/admin/interest" },
    {
      label: "Express interest (URL preview)",
      icon: IconSparkles,
      path: "/admin/express-interest-preview",
      exact: true,
    },
    { label: "Resource", icon: IconFileDescription, path: "/admin/resource" },
    { label: "Authenticator (2FA)", icon: IconShieldLock, path: "/admin/two-factor" },
  ];

  const showVerificationBanner =
    verificationQueueTotal != null &&
    verificationQueueTotal > 0 &&
    !location.pathname.startsWith("/admin/pending-verification");

  const handleNavClick = (path: string) => {
    navigate(path);
    closeMobileNav();
  };

  const handleLogout = () => {
    Cookies.remove("token");
    navigate("/admin/login");
  };

  return (
    <AppShell
      header={{ height: { base: 56, sm: 0 } }}
      navbar={{
        width: 260,
        breakpoint: "sm",
        collapsed: { mobile: !mobileNavOpened, desktop: false },
      }}
      padding="0"
    >
      <AppShell.Header hiddenFrom="sm">
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Image src="/gtLogo.svg" alt="GTsetu" style={{ height: 34, width: "auto", flexShrink: 0 }} />
            <Badge color="red" size="sm" variant="filled" style={{ flexShrink: 0 }}>
              Admin
            </Badge>
          </Group>
          <ActionIcon
            variant="subtle"
            color="gray.8"
            size="lg"
            radius="md"
            onClick={toggleMobileNav}
            aria-label={mobileNavOpened ? "Close navigation" : "Open navigation"}
          >
            {mobileNavOpened ? <IconX size={22} stroke={2} /> : <AdminNavMenuClosedIcon size={22} />}
          </ActionIcon>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        {/* HEADER / BRANDING (mobile uses AppShell.Header; avoid duplicate) */}
        <AppShell.Section mb="md" visibleFrom="sm">
          <Group gap="xs">
            <Image src="/gtLogo.svg" alt="GTsetu" style={{ height: 38, width: "auto" }} />
            <Badge color="red" size="sm" variant="filled">
              Admin
            </Badge>
          </Group>
          <Divider />
        </AppShell.Section>

        {/* NAV LINKS */}
        <AppShell.Section grow component={ScrollArea}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {navItems.map((item) => (
              <NavLink
                key={item.label}
                label={
                  <Group gap={8} wrap="nowrap" justify="space-between" w="100%">
                    <Text
                      size="sm"
                      fw={400}
                      c={isActive(item.path, item.exact) ? "primary.7" : undefined}
                      style={{ flex: 1 }}
                    >
                      {item.label}
                    </Text>
                    {item.badgeCount != null && item.badgeCount > 0 ? (
                      <Badge size="sm" variant="filled" color="orange" circle>
                        {item.badgeCount > 99 ? "99+" : item.badgeCount}
                      </Badge>
                    ) : null}
                  </Group>
                }
                leftSection={
                  <item.icon
                    size="1.2rem"
                    stroke={1.8}
                    color={isActive(item.path, item.exact) ? "var(--mantine-color-primary-7)" : undefined}
                  />
                }
                active={isActive(item.path, item.exact)}
                onClick={() => handleNavClick(item.path)}
                variant="light"
                color="primary.7"
                styles={{
                  root: {
                    borderRadius: 8,
                    padding: "10px 12px",
                  },
                }}
              />
            ))}
          </div>
        </AppShell.Section>

        {/* ADMIN PROFILE */}
        <AppShell.Section>
          <Menu shadow="md" width={250} position="top-end">
            <Menu.Target>
              <UnstyledButton
                w="100%"
                p="xs"
                style={{
                  borderRadius: 8,
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-1)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <Group>
                  <Avatar color="red" radius="xl">
                    <IconUser size={18} />
                  </Avatar>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <Text size="sm" fw={400} truncate>
                      Admin
                    </Text>
                    <Text c="slate.6" size="xs" truncate>
                      {adminEmail}
                    </Text>
                  </div>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={handleLogout}>
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {showVerificationBanner ? (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="orange"
            variant="light"
            title="Companies in verification"
            styles={{ root: { borderRadius: 0 } }}
          >
            <Text size="sm">
              {verificationQueueTotal === 1
                ? "1 company is"
                : `${verificationQueueTotal} companies are`}{" "}
              in under-review or system-verified status and may need your attention.{" "}
              <Anchor
                size="sm"
                fw={500}
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/admin/pending-verification");
                }}
              >
                Open verification queue
              </Anchor>
            </Text>
          </Alert>
        ) : null}
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
